import { useState } from 'react';

import { useAgentSetupContext } from 'contexts/AgentSetupContext';

import { useRouter } from '@polyai/common/hooks/useRouter';
import { ArrowLeftIcon } from '@polyai/ui/components/atoms/Icons';
import Text from '@polyai/ui/components/atoms/Text';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import { useIsPlgProd } from 'hooks/useIsPlgProd';
import useProjectMutations from 'hooks/useProjectMutations';
import useProjectRoute from 'hooks/useProjectRoute';
import { generateNextPath } from 'lib/routeHelper';
import AGENT_WIZARD_PATHS from '../../paths';
import * as Styled from './ResetButton.styled';
import { RESET_BUTTON_MODAL_COPY } from './ResetButton.uiCopy';

interface ResetButtonProps {
  /**
   * URL to navigate to after successful project deletion
   * @default company route
   */
  redirectUrl?: string;
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
}

const ResetButton = ({ redirectUrl, disabled = false }: ResetButtonProps) => {
  const { pushUrl } = useRouter();
  const { deleteProject, isDeletingProject } = useProjectMutations();
  const { setupStatus, cancelSetup } = useAgentSetupContext();
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const isPlgProd = useIsPlgProd();

  const { projectId, accountId } = useProjectRoute();

  const finalRedirectUrl =
    redirectUrl ??
    (isPlgProd
      ? generateNextPath(AGENT_WIZARD_PATHS.COMPANY_ACCOUNT, { accountId })
      : AGENT_WIZARD_PATHS.COMPANY);

  const handleResetClick = () => {
    // If there's no setup in progress, just redirect without confirmation
    if (!setupStatus) {
      pushUrl(finalRedirectUrl);
      return;
    }

    // Otherwise show confirmation modal
    setShowBackConfirmModal(true);
  };

  const handleConfirmReset = () => {
    // Cancel any ongoing setup before deleting
    cancelSetup();

    // Delete the project
    deleteProject(
      { projectId },
      {
        onSuccess: () => {
          // Navigate to the specified redirect URL
          pushUrl(finalRedirectUrl);
        },
        onError: error => {
          console.error('Failed to delete project:', error);
          // Still navigate on error to prevent user from being stuck
          pushUrl(finalRedirectUrl);
        },
      },
    );
  };

  const handleCancelReset = () => {
    setShowBackConfirmModal(false);
  };

  return (
    <>
      <Styled.ResetButton
        data-test-id="agent-wizard-reset-button"
        disabled={disabled}
        onClick={handleResetClick}
      >
        <ArrowLeftIcon />
      </Styled.ResetButton>

      <ConfirmationModal
        buttonText={RESET_BUTTON_MODAL_COPY.confirmButtonText}
        cancelButtonVariant="GHOST"
        cancelText={RESET_BUTTON_MODAL_COPY.cancelButtonText}
        closeOnDocumentClick={false}
        closeOnEscape={false}
        confirmButtonVariant="PRIMARY"
        confirmDisabled={isDeletingProject}
        content={
          <Text color="textSecondary">
            {RESET_BUTTON_MODAL_COPY.modalContent}
          </Text>
        }
        data-test-id="agent-wizard-reset-modal"
        open={showBackConfirmModal}
        size="sm"
        title={RESET_BUTTON_MODAL_COPY.modalTitle}
        onCancelButtonClick={handleCancelReset}
        onConfirmButtonClick={handleConfirmReset}
      />
    </>
  );
};

export default ResetButton;
